import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { FreelancerSuggestionCard } from "./FreelancerSuggestionCard";
import { DualFreelancerCardsList } from "./DualFreelancerCardsList";
import { getMessageCardsPage } from "@/services/agents-api";
import { reportApiError } from "@/lib/report-error";
import type { FreelancerSugerido, CardsMeta } from "./useVagaAssistant";

interface PaginatedFreelancerCardsProps {
  /** ID da mensagem no servidor pra buscar páginas; ausente = sem paginação. */
  messageId?: string;
  /** Página 1 já carregada (lista primária). */
  initialPrimary: FreelancerSugerido[];
  /** Página 1 já carregada (lista "outras opções"), quando dual. */
  initialExploratory?: FreelancerSugerido[];
  cardsMeta?: CardsMeta;
  onSelecionados?: (freelancerId: string) => void;
  onShortlist?: (freelancerId: string) => void;
  onDescartar?: (freelancer: FreelancerSugerido) => void;
  isProcessing?: boolean;
}

interface PageData {
  primary: FreelancerSugerido[];
  exploratory: FreelancerSugerido[];
}

/**
 * Renderiza a lista de freelancers de uma busca paginada inline. A página 1 já
 * vem embutida (do SSE ou do reabrir); as demais são buscadas sob demanda pelo
 * endpoint de paginação (o motor de recomendação não roda de novo). Renderiza
 * só a página visível, mantendo a performance mesmo com dezenas de perfis.
 *
 * Use `key={messageId}` no componente pai pra resetar o estado numa nova busca.
 */
export function PaginatedFreelancerCards({
  messageId,
  initialPrimary,
  initialExploratory,
  cardsMeta,
  onSelecionados,
  onShortlist,
  onDescartar,
  isProcessing,
}: PaginatedFreelancerCardsProps) {
  const pageSize = cardsMeta?.page_size ?? 10;
  const totalPrimary = cardsMeta?.total_primary ?? initialPrimary.length;
  const totalExploratory = cardsMeta?.total_exploratory ?? (initialExploratory?.length ?? 0);
  const isDual = totalExploratory > 0;
  const total = Math.max(totalPrimary, totalExploratory);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [cache, setCache] = useState<Record<number, PageData>>(() => ({
    1: { primary: initialPrimary, exploratory: initialExploratory ?? [] },
  }));

  const goToPage = useCallback(
    async (target: number) => {
      if (target < 1 || target > totalPages || target === page || loading) return;
      if (cache[target]) {
        setPage(target);
        return;
      }
      if (!messageId) return; // sem id no servidor não dá pra buscar mais páginas
      setLoading(true);
      try {
        const res = await getMessageCardsPage(messageId, target, pageSize);
        setCache((prev) => ({
          ...prev,
          [target]: { primary: res.primary || [], exploratory: res.exploratory || [] },
        }));
        setPage(target);
      } catch (error) {
        reportApiError(error, { source: "vaga_assistant", kind: "cards_page" });
      } finally {
        setLoading(false);
      }
    },
    [messageId, page, totalPages, pageSize, cache, loading],
  );

  const current = cache[page] ?? { primary: [], exploratory: [] };
  const start = (page - 1) * pageSize;
  const shownEnd = Math.min(page * pageSize, total);

  return (
    <div className="space-y-2">
      {loading ? (
        <div className="flex items-center justify-center py-6 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Carregando…
        </div>
      ) : isDual ? (
        <DualFreelancerCardsList
          primary={current.primary}
          exploratory={current.exploratory}
          idsPrimary={cardsMeta?.ids_primary ?? current.primary.map((f) => f.id)}
          idsExploratory={cardsMeta?.ids_exploratory ?? current.exploratory.map((f) => f.id)}
          novosLearnedCount={cardsMeta?.novos_learned_count ?? 0}
          onSelecionados={onSelecionados}
          onShortlist={onShortlist}
          onDescartar={onDescartar}
          isProcessing={isProcessing}
        />
      ) : (
        <div className="space-y-2">
          {current.primary.map((f) => (
            <FreelancerSuggestionCard
              key={f.id}
              freelancer={f}
              onSelecionados={onSelecionados}
              onShortlist={onShortlist}
              onDescartar={onDescartar}
              isProcessing={isProcessing}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-2 pt-1">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs gap-1"
            onClick={() => goToPage(page - 1)}
            disabled={page <= 1 || loading}
          >
            <ChevronLeft className="h-3 w-3" />
            Anterior
          </Button>
          <span className="text-[11px] text-muted-foreground">
            Mostrando {start + 1}-{shownEnd} de {total}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs gap-1"
            onClick={() => goToPage(page + 1)}
            disabled={page >= totalPages || loading}
          >
            Próxima
            <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
}
